import React, { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { collection, doc, onSnapshot, query, where, orderBy, limit } from 'firebase/firestore';
import { Backdrop, CircularProgress } from '@mui/material';
import auth from '../firebaseSetup/auth';
import { firestore } from '../firebaseSetup/firebase';

// Verify firestore is initialized
if (!firestore) {
  console.error('Firestore is not initialized!');
}

const UserContext = createContext(null);

const UserProvider = ({ children }) => {
  const [user, setUser] = useState();
  const [followers, setFollowers] = useState([]);
  const [following, setFollowing] = useState([]);
  const [requests, setRequests] = useState([]);
  const [pendingRequests, setPendingRequests] = useState([]);
  const [unreadNotifications, setUnreadNotifications] = useState(0);
  const [lastAppOpen, setLastAppOpen] = useState(() => {
    const stored = localStorage.getItem('lastAppOpen');
    return stored ? new Date(stored) : null;
  });
  const [addFollowerOpen, setAddFollowerOpen] = useState(false);
  const [followUserOpen, setFollowUserOpen] = useState(false);
  const [notificationsDrawerOpen, setNotificationsDrawerOpen] = useState(false);
  const [tappedNotification, setTappedNotification] = useState(null);
  const [loadingUser, setLoadingUser] = useState(true);
  const loadingRef = useRef();

  useEffect(() => {
    setLoadingUser(true);
    clearTimeout(loadingRef.current);
    let unsubListener = () => {};
    let unsubPrivateListener = () => {};
    const unregisterAuthObserver = auth.onAuthStateChanged((u) => {
      if (u?.uid) {
        // Listen to public profile
        unsubListener = onSnapshot(doc(firestore, 'users', u.uid), (d) => {
          setUser((prev) => ({ ...prev, ...d.data() }));
        }, (error) => {
          console.error('Error listening to user profile:', error);
        });
        // Listen to private data
        unsubPrivateListener = onSnapshot(doc(firestore, 'users', u.uid, 'private', 'data'), (d) => {
          setUser((prev) => ({ ...prev, ...d.data() }));
        }, (error) => {
          console.error('Error listening to user private data:', error);
        });
      } else {
        unsubListener();
        unsubPrivateListener();
        setUser({});
      }
      loadingRef.current = setTimeout(() => {
        setLoadingUser(false);
      }, 1000);
    });

    return () => { unsubListener(); unsubPrivateListener(); unregisterAuthObserver(); };
  }, []);

  // Function to update lastAppOpen (called when user acknowledges notifications)
  const updateLastAppOpen = useCallback(() => {
    const now = new Date().toISOString();
    localStorage.setItem('lastAppOpen', now);
    setLastAppOpen(new Date(now));
  }, []);

  // Listen for unread notifications count
  useEffect(() => {
    if (!user?.uid || typeof user.uid !== 'string') {
      setUnreadNotifications(0);
      return () => {};
    }

    const unreadQuery = query(
      collection(firestore, 'userNotifications', user.uid, 'items'),
      where('read', '==', false),
    );

    const unsub = onSnapshot(unreadQuery, (snapshot) => {
      setUnreadNotifications(snapshot.size);
    }, () => {
      // Silently ignore errors (collection may not exist yet)
      setUnreadNotifications(0);
    });

    return unsub;
  }, [user?.uid]);

  useEffect(() => {
    if (!user?.uid || typeof user.uid !== 'string') return () => {};

    let followersQuery;
    try {
      followersQuery = query(
        collection(firestore, 'users'),
        where('following', 'array-contains', user.uid),
      );
    } catch (e) {
      console.error('Error creating followers query:', e);
      return () => {};
    }

    const followerPrivateSubs = [];
    const unsubFollowers = onSnapshot(followersQuery, (snapshot) => {
      const followersData = snapshot.docs.map((d) => d.data());
      setFollowers(followersData);

      // Also listen to each follower's private data
      followerPrivateSubs.forEach((unsub) => unsub());
      followerPrivateSubs.length = 0;
      followersData.forEach((follower) => {
        if (!follower?.uid) return;
        const unsubPrivate = onSnapshot(doc(firestore, 'users', follower.uid, 'private', 'data'), (privateDoc) => {
          if (privateDoc.exists()) {
            setFollowers((old) => {
              const existing = old.find((f) => f.uid === follower.uid);
              return old.filter((f) => f.uid !== follower.uid).concat({ ...existing, ...privateDoc.data() });
            });
          }
        }, () => {
          // Expected to fail if we don't follow them back - silently ignore
        });
        followerPrivateSubs.push(unsubPrivate);
      });
    });

    const subscriptions = [];

    const followingList = Array.from(new Set(user?.following || [])).filter((uid) => uid && typeof uid === 'string');
    followingList.forEach((followedUid) => {
      if (!followedUid || typeof followedUid !== 'string') return;
      // Listen to public profile
      const unsubFollowing = onSnapshot(doc(firestore, 'users', followedUid), (d) => {
        const newF = d.data();
        setFollowing((old) => {
          const existing = old.find((f) => f.uid === followedUid);
          return old.filter((f) => f.uid !== followedUid).concat({ ...existing, ...newF });
        });
      });
      subscriptions.push(unsubFollowing);

      // Listen to private data (count, genders) - only accessible if they follow us back
      const unsubFollowingPrivate = onSnapshot(doc(firestore, 'users', followedUid, 'private', 'data'), (d) => {
        if (d.exists()) {
          const privateData = d.data();
          setFollowing((old) => {
            const existing = old.find((f) => f.uid === followedUid);
            // Merge private data while preserving uid and other public fields
            return old.filter((f) => f.uid !== followedUid).concat({ uid: followedUid, ...existing, ...privateData });
          });
        }
      }, () => {
        // Expected to fail if they don't follow us back - silently ignore
      });
      subscriptions.push(unsubFollowingPrivate);
    });

    let requestsQuery;
    let pendingRequestsQuery;
    try {
      requestsQuery = query(
        collection(firestore, 'requests'),
        where('to', '==', user.uid),
      );
      pendingRequestsQuery = query(
        collection(firestore, 'requests'),
        where('from', '==', user.uid),
      );
    } catch (e) {
      console.error('Error creating requests queries:', e);
      return () => { unsubFollowers(); subscriptions.forEach((unsub) => unsub()); followerPrivateSubs.forEach((unsub) => unsub()); };
    }

    const unsubRequests = onSnapshot(requestsQuery, (snapshot) => {
      const requestsData = snapshot.docs.map((d) => d.data());
      setRequests(requestsData);
    });

    const unsubPendingRequests = onSnapshot(pendingRequestsQuery, (snapshot) => {
      const pendingRequestsData = snapshot.docs.map((d) => d.data());
      setPendingRequests(pendingRequestsData);
    });

    return () => {
      unsubFollowers();
      unsubRequests();
      unsubPendingRequests();
      setFollowing([]);
      setFollowers([]);
      subscriptions.forEach((unsub) => unsub());
      followerPrivateSubs.forEach((unsub) => unsub());
    };
  }, [user?.following, user?.uid]);

  const value = useMemo(() => {
    return {
      user,
      followers,
      following,
      requests,
      pendingRequests,
      unreadNotifications,
      lastAppOpen,
      updateLastAppOpen,
      addFollowerOpen,
      followUserOpen,
      notificationsDrawerOpen,
      tappedNotification,
      loadingUser,
      setAddFollowerOpen,
      setFollowUserOpen,
      setNotificationsDrawerOpen,
      setTappedNotification,
    };
  }, [user, followers, loadingUser, following, requests, pendingRequests, unreadNotifications, lastAppOpen, updateLastAppOpen, addFollowerOpen, followUserOpen, notificationsDrawerOpen, tappedNotification]);

  return (
    <UserContext.Provider value={value}>
      <Backdrop open={loadingUser} sx={(theme) => ({ zIndex: theme.zIndex.drawer + 1, color: '#fff', display: 'flex', flexDirection: 'column', alignItems: 'center', backgroundColor: theme.palette.background.default })}>
        <CircularProgress color="primary" sx={{ mb: 2 }} />
        Loading
      </Backdrop>
      {children}
    </UserContext.Provider>
  );
};

const useUser = () => {
  const context = useContext(UserContext);

  if (!context) {
    throw new Error('useUser must be used within a UserProvider');
  }

  return context;
};

export { UserProvider, UserContext, useUser };
